import {Box, InputLabel, Slider, Tooltip} from "@mui/material";
import {memo, useEffect, useState, useCallback, useRef} from "react";
import dayjs from "dayjs";
import {useTheme} from "@mui/material/styles";

const CustomValueLabel = (props) => {
  const theme = useTheme()
  const { children, open, value, computeZoomBoxStyle, zoomStates, zoomMin, zoomMax, t0, t1} = props;

  return (
    <Tooltip
      open={open}
      enterTouchDelay={0}
      placement="top"
      // slots={{
      //     transition: Zoom,
      //   }}
      slotProps={{
        popper: {
          modifiers: [
            {
              name: 'offset',
              options: {
                offset: [0, -16],
              },
            },
                    {
          name: 'preventOverflow',
          options: {
            boundary: 'scrollParent',
            padding: 8
          }
        },
          ],
        },
      }}
      componentsProps={{
        tooltip: {
          sx: {
            backgroundColor: 'transparent',
          }
        },
        arrow: {
          sx: {
            color: 'transparent'
          }
        }
      }}
      title={
        <Box>
          <Box
            sx={{
              fontSize: '10px',
              fontWeight: 'bold',
              textAlign: 'center',
              textShadow: '0px 0px 3px rgba(255, 255, 255, 1)',
              color: theme.palette.blues.rothko2,
            }}
          >
            {dayjs.unix(value / 1000).format('YYYY-MM-DD HH:mm:ss')}
          </Box>
          {zoomStates.length > 1 &&
            <Box
              sx={{
                position: 'relative',
                width: '120px',
                height: '35px',
                overflow: 'hidden',
                borderRadius: '2px'
              }}>

            {zoomStates.map((state) => (
              <Box key={state.id} style={computeZoomBoxStyle(state)} />
            ))}
              <Box sx={{
                position: 'absolute',
                left: `${((dayjs(t0).valueOf() - zoomMin) / (zoomMax - zoomMin)) * 100}%`,
                width: `${((dayjs(t1).valueOf() - dayjs(t0).valueOf()) / (zoomMax - zoomMin)) * 100}%`,
                height: `100%`,
                backgroundColor: '#34775c',
                opacity: 0.75,
                borderRadius: '3px',
                border: '.1px black solid',
                }}
              />
            <Box
              sx={{
                position: 'absolute',
                left: '50%',
                top: 0,
                bottom: 0,
                width: '2px',
                backgroundColor: theme.palette.reds.rothko1,
              }}
            />
            </Box>
          }
        </Box>
      }
    >
      {children}
    </Tooltip>
  );
};


const MhdTimeSlider = ({ label, t0, t1, ti, error, onChange, disabled, states, selectedState }) => {
  const theme = useTheme()
  const [minTime, setMinTime] = useState(0);
  const [maxTime, setMaxTime] = useState(1);
  const [value, setValue] = useState(dayjs(ti).valueOf());
  const [zoomWindow, setZoomWindow] = useState(1);
  const [zoomStates, setZoomStates] = useState([]);
  const [zoomMin, setZoomMin] = useState(value - zoomWindow);
  const [zoomMax, setZoomMax] = useState(value + zoomWindow);
  const [t0value, setT0Value] = useState(dayjs(t0).valueOf());
  const [t1value, setT1Value] = useState(dayjs(t1).valueOf());

  useEffect(() => {
    if (states.length > 0) {
      setMinTime(dayjs(states[0].state_t0).valueOf());
      setMaxTime(dayjs(states[states.length - 1].state_t1).valueOf());
      setZoomWindow(4 * (dayjs(states[states.length - 1].state_t1).valueOf() - dayjs(states[states.length - 1].state_t0).valueOf()));
    }
  }, [states]);

  useEffect(() => {
    setValue(dayjs(ti).valueOf())
  }, [ti]);

  useEffect(() => {
    setT0Value(dayjs(t0).valueOf())
  }, [t0]);

  useEffect(() => {
    setT1Value(dayjs(t1).valueOf())
  }, [t1]);

  useEffect(() => {
    setZoomMin(value - zoomWindow);
    setZoomMax(value + zoomWindow);
  }, [value]);

  useEffect(() => {
    const newZoomStates = states.filter(state => {
      const st0 = dayjs(state.state_t0).valueOf();
      const st1 = dayjs(state.state_t1).valueOf();
      return st1 >= zoomMin && st0 <= zoomMax;
    });
    setZoomStates(newZoomStates)
  }, [zoomMin, zoomMax, states]);

  const computeBoxStyle = (state, overallMin, overallMax) => {
    const startVal = dayjs(state.state_t0).valueOf();
    const endVal = dayjs(state.state_t1).valueOf();
    const leftPercent = ((startVal - overallMin) / (overallMax - overallMin)) * 100;
    const widthPercent = ((endVal - startVal) / (overallMax - overallMin)) * 100;
    const isOverlapping = state.id === selectedState;
    return {
      position: 'absolute',
      left: `${leftPercent}%`,
      width: `${widthPercent}%`,
      height: '100%',
      backgroundColor: isOverlapping ? `${theme.palette.blues.rothko1}` : `${theme.palette.blues.rothko4}`,
      opacity: 1,
      borderRadius: '3px',
      border: '.1px black solid',
      boxShadow: isOverlapping ? 'inset 1px 1px 2px rgba(0, 0, 0, 0.7), inset -1px -1px 2px rgba(0, 0, 0, 0.7)' : 'none',
    };
  };

  return (
    <Box position="relative" width="100%">
      <InputLabel variant={error ? 'error' : 'primary'}>
        {label}
      </InputLabel>
      <Slider
        value={value}
        onChange={useCallback((e, nv) => {onChange(dayjs.unix(nv / 1000).format('YYYY-MM-DD HH:mm:ss'))})}
        valueLabelDisplay="auto"
        step={60000}
        min={minTime}
        max={maxTime}
        size="small"
        disableSwap
        disabled={disabled}
        slots={{ valueLabel: CustomValueLabel }}
        slotProps={{
          valueLabel: {
            value: value,
            computeZoomBoxStyle: useCallback((state) => computeBoxStyle(state, zoomMin, zoomMax), [zoomMin, zoomMax]),
            zoomStates: zoomStates,
            zoomMin: zoomMin,
            zoomMax: zoomMax,
            zoomWindow: zoomWindow,
            t0: t0value,
            t1: t1value,
          },
        }}
        sx={{
          zIndex: 2,
          marginTop: '-6px',
          '& .MuiSlider-thumb': {
            top: '0px',
            borderRadius: 0,
            background: 'transparent',
            width: 0,
            height: 0,
            borderLeft: '4px solid transparent',
            borderRight: '4px solid transparent',
            borderTop: `6px solid ${theme.palette.blues.rothko2}`,
            boxShadow: 'none!important',
          },
          '& .MuiSlider-track': {
            color: 'transparent'
          },
          '& .MuiSlider-rail': {
            color: 'transparent'
          },
        }}
      />
      <Box
        position="absolute"
        left={0}
        right={0}
        top="35%"
        style={{ height: '65%'}}
      >
        {states.map((state) => (
            <Box key={state.id} style={computeBoxStyle(state, minTime, maxTime)} />
        ))}
      </Box>
    </Box>
  );
};

export default memo(MhdTimeSlider);